import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  HeartHandshake,
  Pill,
  Mail,
  Sparkles,
  Tag,
  Gift,
  Star,
  Stethoscope,
  Leaf,
  Heart,
  Baby,
  Dumbbell,
  Sun,
  UserRound,
  Scissors,
  HandHeart,
} from "lucide-react";
import { toast } from "sonner";
import api, { imgUrl } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import ProductCard from "../components/storefront/ProductCard";

const SITE_URL = "https://farmakeiokatirtzoglou.com";

/*
 * =========================================================
 * SEO META HELPER
 * =========================================================
 */

function setMetaTag(attribute, value, content) {
  if (!content) return;

  let element = document.head.querySelector(
    `meta[${attribute}="${value}"]`
  );

  if (!element) {
    element = document.createElement("meta");

    if (attribute === "property") {
      element.setAttribute("property", value);
    } else {
      element.setAttribute("name", value);
    }

    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function setCanonical(url) {
  let canonical = document.head.querySelector(
    'link[rel="canonical"]'
  );

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute("href", url);
}

function setStructuredData(id, data) {
  let script = document.head.querySelector(
    `script[data-seo-schema="${id}"]`
  );

  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo-schema", id);
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

/*
 * =========================================================
 * CATEGORY ICONS
 * =========================================================
 */

const categoryIcons = {
  "sun-care": Sun,
  "face-care": Sparkles,
  "gift-moments": Gift,
  "womens-care": Heart,
  "kids-care": Baby,
  "dietary-supplements-vitamins": Pill,
  "sports-nutrition-healthy-snacks": Dumbbell,
  accessories: Star,
  "hair-care": Scissors,
  "insect-repellent": Leaf,
  "men-essentials": UserRound,
  "body-care": HandHeart,
  "personal-hygiene": HeartHandshake,
  "first-aid-relief": Stethoscope,
};

/*
 * =========================================================
 * THEMATIC CATEGORY HELPERS
 * =========================================================
 */

const thematicCategories = [
  {
    match: [
      "dietary-supplements-vitamins",
      "vitamins",
      "supplements",
    ],
    title: "Βιταμίνες & Συμπληρώματα",
    text: "Καθημερινή φροντίδα για ενέργεια, ευεξία και ισορροπημένη διατροφή.",
    icon: Pill,
  },
  {
    match: ["sun-care", "ilios-frontida", "sun"],
    title: "Ήλιος & Προστασία",
    text: "Αντηλιακή προστασία και φροντίδα πριν και μετά τον ήλιο.",
    icon: Sun,
  },
  {
    match: ["face-care", "prosopo"],
    title: "Περιποίηση Προσώπου",
    text: "Προϊόντα για καθημερινή περιποίηση και φροντίδα της επιδερμίδας.",
    icon: Sparkles,
  },
  {
    match: ["kids-care", "kids", "paidi"],
    title: "Φροντίδα για Παιδιά",
    text: "Επιλεγμένα προϊόντα για την καθημερινή φροντίδα των μικρών μας.",
    icon: Baby,
  },
  {
    match: ["womens-care", "gynaikeia"],
    title: "Γυναικεία Φροντίδα",
    text: "Λύσεις για την καθημερινή προσωπική φροντίδα κάθε γυναίκας.",
    icon: Heart,
  },
  {
    match: ["men-essentials", "andrika"],
    title: "Men Essentials",
    text: "Καθημερινή περιποίηση και προσωπική φροντίδα για τον άνδρα.",
    icon: UserRound,
  },
];

/*
 * =========================================================
 * HOME
 * =========================================================
 */

export default function Home() {
  const { settings } = useSettings();

  const [featured, setFeatured] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [email, setEmail] = useState("");

  /*
   * =========================================================
   * SEO
   * =========================================================
   */

  useEffect(() => {
    const title =
      "Φαρμακείο Κατιρτζόγλου | Online Φαρμακείο & Παραφαρμακευτικά";

    const description =
      "Φαρμακείο Κατιρτζόγλου – Online φαρμακείο με προϊόντα υγείας, ομορφιάς, περιποίησης, βιταμίνες, συμπληρώματα διατροφής και παραφαρμακευτικά προϊόντα.";

    const canonicalUrl = `${SITE_URL}/`;

    /*
     * -------------------------------------------------------
     * BASIC SEO
     * -------------------------------------------------------
     */

    document.title = title;

    setMetaTag(
      "name",
      "description",
      description
    );

    setMetaTag(
      "name",
      "robots",
      "index, follow, max-image-preview:large"
    );

    setMetaTag(
      "name",
      "googlebot",
      "index, follow, max-image-preview:large"
    );

    /*
     * -------------------------------------------------------
     * OPEN GRAPH
     * -------------------------------------------------------
     */

    setMetaTag(
      "property",
      "og:title",
      title
    );

    setMetaTag(
      "property",
      "og:description",
      description
    );

    setMetaTag(
      "property",
      "og:type",
      "website"
    );

    setMetaTag(
      "property",
      "og:url",
      canonicalUrl
    );

    setMetaTag(
      "property",
      "og:image",
      `${SITE_URL}/logo.jpeg`
    );

    setMetaTag(
      "property",
      "og:image:alt",
      "Φαρμακείο Κατιρτζόγλου"
    );

    setMetaTag(
      "property",
      "og:locale",
      "el_GR"
    );

    /*
     * -------------------------------------------------------
     * TWITTER
     * -------------------------------------------------------
     */

    setMetaTag(
      "name",
      "twitter:card",
      "summary_large_image"
    );

    setMetaTag(
      "name",
      "twitter:title",
      title
    );

    setMetaTag(
      "name",
      "twitter:description",
      description
    );

    setMetaTag(
      "name",
      "twitter:image",
      `${SITE_URL}/logo.jpeg`
    );

    /*
     * -------------------------------------------------------
     * CANONICAL
     * -------------------------------------------------------
     */

    setCanonical(canonicalUrl);

    /*
     * -------------------------------------------------------
     * WEBSITE SCHEMA
     * -------------------------------------------------------
     */

    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      name: "Φαρμακείο Κατιρτζόγλου",
      url: canonicalUrl,
      description,
      inLanguage: "el-GR",

      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate:
            `${SITE_URL}/shop?search={search_term_string}`,
        },
        "query-input":
          "required name=search_term_string",
      },
    };

    setStructuredData(
      "website-schema",
      websiteSchema
    );

    /*
     * -------------------------------------------------------
     * PHARMACY SCHEMA
     * -------------------------------------------------------
     */

    const pharmacySchema = {
      "@context": "https://schema.org",
      "@type": "Pharmacy",
      "@id": `${SITE_URL}/#pharmacy`,

      name: "Φαρμακείο Κατιρτζόγλου",

      url: canonicalUrl,

      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/logo.jpeg`,
      },

      image: `${SITE_URL}/logo.jpeg`,

      description,

      address: {
        "@type": "PostalAddress",
        addressLocality: "Ελαιώνας",
        addressRegion: "Σέρρες",
        postalCode: "62100",
        addressCountry: "GR",
      },

      areaServed: {
        "@type": "Country",
        name: "Ελλάδα",
      },
    };

    setStructuredData(
      "pharmacy-schema",
      pharmacySchema
    );

    /*
     * -------------------------------------------------------
     * WEBPAGE SCHEMA
     * -------------------------------------------------------
     */

    const webpageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${SITE_URL}/#webpage`,

      url: canonicalUrl,

      name: title,

      description,

      isPartOf: {
        "@id": `${SITE_URL}/#website`,
      },

      about: {
        "@id": `${SITE_URL}/#pharmacy`,
      },

      inLanguage: "el-GR",
    };

    setStructuredData(
      "webpage-schema",
      webpageSchema
    );

    /*
     * -------------------------------------------------------
     * CLEANUP
     * -------------------------------------------------------
     */

    return () => {
      document.head
        .querySelector(
          'script[data-seo-schema="website-schema"]'
        )
        ?.remove();

      document.head
        .querySelector(
          'script[data-seo-schema="pharmacy-schema"]'
        )
        ?.remove();

      document.head
        .querySelector(
          'script[data-seo-schema="webpage-schema"]'
        )
        ?.remove();
    };
  }, []);

  /*
   * =========================================================
   * LOAD DATA
   * =========================================================
   */

  useEffect(() => {
    Promise.all([
      api
        .get("/products")
        .then(({ data }) => {
          setAllProducts(
            Array.isArray(data) ? data : []
          );
        })
        .catch(() => {}),

      api
        .get("/products", {
          params: {
            featured: true,
          },
        })
        .then(({ data }) => {
          setFeatured(
            Array.isArray(data)
              ? data.slice(0, 8)
              : []
          );
        })
        .catch(() => {}),

      api
        .get("/categories")
        .then(({ data }) => {
          setCategories(
            Array.isArray(data) ? data : []
          );
        })
        .catch(() => {}),

      api
        .get("/brands")
        .then(({ data }) => {
          setBrands(
            Array.isArray(data) ? data : []
          );
        })
        .catch(() => {}),
    ]);
  }, []);

  const hero = settings?.hero || {};
  const newsletter = settings?.newsletter || {};
  const store = settings?.store_section || {};

  /*
   * =========================================================
   * NEWSLETTER
   * =========================================================
   */

  const subscribe = async (e) => {
    e.preventDefault();

    if (!email) return;

    try {
      await api.post("/newsletter", {
        email,
      });

      toast.success(
        "Εγγραφήκατε με επιτυχία!"
      );

      setEmail("");
    } catch {
      toast.error(
        "Σφάλμα εγγραφής"
      );
    }
  };

  /*
   * =========================================================
   * HOME CATEGORIES
   * =========================================================
   */

  const homeCategories = categories.filter(
    (cat) =>
      (!cat.parent_id ||
        cat.parent_id === "") &&
      cat.show_on_home !== false
  );

  /*
   * =========================================================
   * OFFERS
   * =========================================================
   */

  const offerProducts = allProducts
    .filter(
      (product) =>
        Number(product.compare_price) >
        Number(product.price)
    )
    .slice(0, 8);

  /*
   * =========================================================
   * NEW PRODUCTS
   * =========================================================
   */

  const newProducts =
    allProducts.slice(0, 8);

  /*
   * =========================================================
   * GIFT MOMENTS
   * =========================================================
   */

  const giftCategory = categories.find(
    (cat) =>
      cat.slug === "gift-moments" ||
      cat.name
        ?.toLowerCase()
        .includes("gift")
  );

  /*
   * =========================================================
   * THEMATIC NEEDS
   * =========================================================
   */

  const thematicLinks = thematicCategories
    .map((theme) => {
      const category = categories.find(
        (cat) =>
          theme.match.some(
            (match) =>
              cat.slug
                ?.toLowerCase()
                .includes(match) ||
              cat.name
                ?.toLowerCase()
                .includes(match)
          )
      );

      if (!category) return null;

      return {
        ...theme,
        category,
      };
    })
    .filter(Boolean)
    .slice(0, 6);

  return (
    <div className="bg-[#FAFBF9]">

      {/* =====================================================
          HERO
      ====================================================== */}

      <section className="relative overflow-hidden bg-[#F3F6EF]">

        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-10 items-center py-14 lg:py-20">

          <div className="animate-fade-up">

            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#536D61] mb-4">
              Φαρμακείο · Υγεία · Ευεξία
            </p>

            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#1E3A2F] leading-[1.05]">
              {hero.title ||
                "Η υγεία σας, η φροντίδα μας"}
            </h1>

            <p className="mt-6 text-base sm:text-lg text-[#2a3a33] leading-relaxed max-w-xl">
              {hero.subtitle ||
                "Ανακαλύψτε προϊόντα υγείας, ομορφιάς, περιποίησης, βιταμίνες και συμπληρώματα διατροφής, με τη φροντίδα του Φαρμακείου Κατιρτζόγλου."}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">

              <Link
                to="/shop"
                data-testid="hero-cta"
                className="inline-flex items-center gap-2 bg-[#1E3A2F] text-white px-7 py-3.5 rounded-sm font-medium hover:bg-[#111C18] transition-colors"
              >
                {hero.cta_text ||
                  "Δείτε τα προϊόντα"}

                <ArrowRight className="h-4 w-4" />
              </Link>

              <Link
                to="/shop"
                className="inline-flex items-center gap-2 border border-[#1E3A2F] text-[#1E3A2F] px-7 py-3.5 rounded-sm font-medium hover:bg-white transition-colors"
              >
                Όλες οι κατηγορίες
              </Link>

            </div>
          </div>

          <div
            className="relative animate-fade-up"
            style={{
              animationDelay: "0.15s",
            }}
          >

            <div className="aspect-[4/5] rounded-sm overflow-hidden border border-[#E5E9E4] bg-[#E8F0E1]">

              {hero.image ? (
                <img
                  src={imgUrl(hero.image)}
                  alt={
                    hero.title
                      ? `${hero.title} | Φαρμακείο Κατιρτζόγλου`
                      : "Φαρμακείο Κατιρτζόγλου - Υγεία και ευεξία"
                  }
                  className="h-full w-full object-cover"
                  fetchPriority="high"
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center">
                  <Pill className="h-20 w-20 text-[#1E3A2F]/20" />
                </div>
              )}

            </div>
          </div>

        </div>
      </section>

      {/* =====================================================
          QUICK LINKS
      ====================================================== */}

      <section className="bg-white border-b border-[#E5E9E4]">

        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">

            <Link
              to="/shop?offers=true"
              className="group flex items-center gap-3 p-4 rounded-sm hover:bg-[#FFF4F2] transition-colors"
            >

              <div className="p-2.5 rounded-full bg-[#FFF1EF] text-[#C25953]">
                <Tag className="h-5 w-5" />
              </div>

              <div>
                <p className="font-semibold text-[#1E3A2F]">
                  Προσφορές
                </p>

                <p className="text-xs text-[#687B73]">
                  Ξεχωριστές τιμές
                </p>
              </div>

            </Link>

            <Link
              to="/shop?sort=newest"
              className="group flex items-center gap-3 p-4 rounded-sm hover:bg-[#E8F0E1] transition-colors"
            >

              <div className="p-2.5 rounded-full bg-[#E8F0E1] text-[#1E3A2F]">
                <Sparkles className="h-5 w-5" />
              </div>

              <div>
                <p className="font-semibold text-[#1E3A2F]">
                  Νέα προϊόντα
                </p>

                <p className="text-xs text-[#687B73]">
                  Μόλις προστέθηκαν
                </p>
              </div>

            </Link>

            {giftCategory ? (
              <Link
                to={`/category/${giftCategory.slug}`}
                className="group flex items-center gap-3 p-4 rounded-sm hover:bg-[#F7F1EB] transition-colors"
              >

                <div className="p-2.5 rounded-full bg-[#F7F1EB] text-[#7A5C45]">
                  <Gift className="h-5 w-5" />
                </div>

                <div>
                  <p className="font-semibold text-[#1E3A2F]">
                    Gift Moments
                  </p>

                  <p className="text-xs text-[#687B73]">
                    Δώρα για κάθε στιγμή
                  </p>
                </div>

              </Link>
            ) : (
              <Link
                to="/shop"
                className="group flex items-center gap-3 p-4 rounded-sm hover:bg-[#F7F1EB] transition-colors"
              >

                <div className="p-2.5 rounded-full bg-[#F7F1EB] text-[#7A5C45]">
                  <Gift className="h-5 w-5" />
                </div>

                <div>
                  <p className="font-semibold text-[#1E3A2F]">
                    Gift Moments
                  </p>

                  <p className="text-xs text-[#687B73]">
                    Δώρα για κάθε στιγμή
                  </p>
                </div>

              </Link>
            )}

            <Link
              to="/shop?featured=true"
              className="group flex items-center gap-3 p-4 rounded-sm hover:bg-[#FAF6E8] transition-colors"
            >

              <div className="p-2.5 rounded-full bg-[#FAF6E8] text-[#8A6A1F]">
                <Star className="h-5 w-5" />
              </div>

              <div>
                <p className="font-semibold text-[#1E3A2F]">
                  Επιλεγμένα
                </p>

                <p className="text-xs text-[#687B73]">
                  Προτάσεις μας
                </p>
              </div>

            </Link>

          </div>
        </div>
      </section>

      {/* =====================================================
          TRUST BADGES
      ====================================================== */}

      <section className="border-b border-[#E5E9E4] bg-white">

        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-7 grid grid-cols-1 sm:grid-cols-3 gap-6">

          {[
            {
              icon: Truck,
              t: "Γρήγορη αποστολή",
              s: "Σε όλη την Ελλάδα",
            },
            {
              icon: ShieldCheck,
              t: "Γνήσια προϊόντα",
              s: "Εγγύηση ποιότητας",
            },
            {
              icon: HeartHandshake,
              t: "Εξυπηρέτηση",
              s: "Πάντα δίπλα σας",
            },
          ].map((b, i) => (
            <div
              key={i}
              className="flex items-center gap-4"
            >

              <div className="p-3 rounded-sm bg-[#E8F0E1] text-[#1E3A2F]">
                <b.icon className="h-6 w-6" />
              </div>

              <div>
                <p className="font-semibold text-[#1E3A2F]">
                  {b.t}
                </p>

                <p className="text-sm text-[#536D61]">
                  {b.s}
                </p>
              </div>

            </div>
          ))}

        </div>
      </section>

      {/* =====================================================
          CATEGORIES
      ====================================================== */}

      {homeCategories.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">

          <div className="flex items-end justify-between mb-8">

            <div>

              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#536D61] mb-2">
                Ανακαλύψτε
              </p>

              <h2 className="font-serif-display text-3xl lg:text-4xl text-[#1E3A2F]">
                Κατηγορίες
              </h2>

            </div>

            <Link
              to="/shop"
              className="text-sm text-[#536D61] hover:text-[#1E3A2F] flex items-center gap-1"
            >
              Όλα
              <ArrowRight className="h-4 w-4" />
            </Link>

          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">

            {homeCategories.map((cat) => {

              const Icon =
                categoryIcons[cat.slug] || Pill;

              return (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group relative overflow-hidden rounded-sm border border-[#E5E9E4] aspect-[4/3] bg-[#E8F0E1]"
                  data-testid={`category-card-${cat.id}`}
                >

                  {cat.image ? (
                    <img
                      src={imgUrl(cat.image)}
                      alt={`${cat.name} | Φαρμακείο Κατιρτζόγλου`}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center">
                      <Icon className="h-12 w-12 text-[#1E3A2F]/30" />
                    </div>
                  )}

                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                  <div className="absolute bottom-0 left-0 right-0 p-4">

                    <span className="text-white font-medium text-base sm:text-lg leading-tight">
                      {cat.name}
                    </span>

                    <span className="mt-1 flex items-center gap-1 text-white/80 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                      Ανακάλυψε
                      <ArrowRight className="h-3 w-3" />
                    </span>

                  </div>

                </Link>
              );
            })}

          </div>
        </section>
      )}

      {/* =====================================================
          OFFERS
      ====================================================== */}

      {offerProducts.length > 0 && (
        <section className="bg-[#FFF8F6] border-y border-[#F1DDD8]">

          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">

            <div className="flex items-end justify-between mb-8">

              <div>

                <div className="flex items-center gap-2 mb-2">
                  <Tag className="h-4 w-4 text-[#C25953]" />

                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#C25953]">
                    Ξεχωριστές τιμές
                  </p>
                </div>

                <h2 className="font-serif-display text-3xl lg:text-4xl text-[#1E3A2F]">
                  Προσφορές
                </h2>

              </div>

              <Link
                to="/shop?offers=true"
                className="text-sm text-[#536D61] hover:text-[#1E3A2F] flex items-center gap-1"
              >
                Όλες οι προσφορές
                <ArrowRight className="h-4 w-4" />
              </Link>

            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8">

              {offerProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                />
              ))}

            </div>

          </div>
        </section>
      )}

      {/* =====================================================
          NEW PRODUCTS
      ====================================================== */}

      {newProducts.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">

          <div className="flex items-end justify-between mb-8">

            <div>

              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-[#1E3A2F]" />

                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#536D61]">
                  Μόλις προστέθηκαν
                </p>
              </div>

              <h2 className="font-serif-display text-3xl lg:text-4xl text-[#1E3A2F]">
                Νέα προϊόντα
              </h2>

            </div>

            <Link
              to="/shop?sort=newest"
              className="text-sm text-[#536D61] hover:text-[#1E3A2F] flex items-center gap-1"
            >
              Δείτε όλα
              <ArrowRight className="h-4 w-4" />
            </Link>

          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8">

            {newProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
              />
            ))}

          </div>

        </section>
      )}

      {/* =====================================================
          FEATURED PRODUCTS
      ====================================================== */}

      {featured.length > 0 && (
        <section className="bg-white border-y border-[#E5E9E4]">

          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">

            <div className="flex items-end justify-between mb-8">

              <div>

                <div className="flex items-center gap-2 mb-2">
                  <Star className="h-4 w-4 text-[#8A6A1F]" />

                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#536D61]">
                    Οι προτάσεις μας
                  </p>
                </div>

                <h2 className="font-serif-display text-3xl lg:text-4xl text-[#1E3A2F]">
                  Επιλεγμένα προϊόντα
                </h2>

              </div>

              <Link
                to="/shop?featured=true"
                className="text-sm text-[#536D61] hover:text-[#1E3A2F] flex items-center gap-1"
              >
                Όλα
                <ArrowRight className="h-4 w-4" />
              </Link>

            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8">

              {featured.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                />
              ))}

            </div>

          </div>
        </section>
      )}

      {/* =====================================================
          GIFT MOMENTS
      ====================================================== */}

      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">

        <div className="relative overflow-hidden rounded-sm bg-[#F3EEE8] border border-[#E5DCD2]">

          <div className="grid lg:grid-cols-2 items-center">

            <div className="p-8 sm:p-12">

              <div className="flex items-center gap-2 mb-3">

                <Gift className="h-5 w-5 text-[#7A5C45]" />

                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#7A5C45]">
                  Gift Moments
                </p>

              </div>

              <h2 className="font-serif-display text-3xl sm:text-4xl text-[#1E3A2F] leading-tight">
                Μικρές στιγμές,
                <br />
                όμορφα δώρα
              </h2>

              <p className="mt-4 text-[#536D61] leading-relaxed max-w-lg">
                Ανακαλύψτε επιλογές για δώρα και ξεχωριστές στιγμές,
                για εκείνους που αγαπάτε αλλά και για εσάς.
              </p>

              <Link
                to={
                  giftCategory
                    ? `/category/${giftCategory.slug}`
                    : "/shop"
                }
                className="mt-7 inline-flex items-center gap-2 bg-[#1E3A2F] text-white px-6 py-3 rounded-sm font-medium hover:bg-[#111C18] transition-colors"
              >
                Ανακάλυψε τα Gift Moments
                <ArrowRight className="h-4 w-4" />
              </Link>

            </div>

            <div className="hidden lg:flex items-center justify-center min-h-[330px] bg-[#E8DED3]">
              <Gift className="h-28 w-28 text-[#7A5C45]/30" />
            </div>

          </div>
        </div>
      </section>

      {/* =====================================================
          THEMATIC NEEDS
      ====================================================== */}

      {thematicLinks.length > 0 && (
        <section className="bg-[#F3F6EF] border-y border-[#E5E9E4]">

          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">

            <div className="text-center max-w-2xl mx-auto mb-10">

              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#536D61] mb-2">
                Βρες αυτό που χρειάζεσαι
              </p>

              <h2 className="font-serif-display text-3xl lg:text-4xl text-[#1E3A2F]">
                Φροντίδα ανά ανάγκη
              </h2>

              <p className="mt-3 text-sm sm:text-base text-[#536D61] leading-relaxed">
                Επιλέξτε την κατηγορία που σας ενδιαφέρει και
                ανακαλύψτε προϊόντα για την καθημερινή σας φροντίδα.
              </p>

            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">

              {thematicLinks.map((item) => {

                const Icon = item.icon;

                return (
                  <Link
                    key={item.category.id}
                    to={`/category/${item.category.slug}`}
                    className="group bg-white border border-[#E5E9E4] rounded-sm p-5 hover:border-[#CBD9B6] hover:shadow-sm transition-all"
                  >

                    <div className="flex items-start justify-between gap-4">

                      <div className="p-3 rounded-sm bg-[#E8F0E1] text-[#1E3A2F]">
                        <Icon className="h-5 w-5" />
                      </div>

                      <ArrowRight className="h-4 w-4 text-[#89978F] group-hover:text-[#1E3A2F] group-hover:translate-x-1 transition-all" />

                    </div>

                    <h3 className="mt-5 font-semibold text-[#1E3A2F]">
                      {item.title}
                    </h3>

                    <p className="mt-2 text-xs sm:text-sm text-[#687B73] leading-relaxed">
                      {item.text}
                    </p>

                  </Link>
                );
              })}

            </div>
          </div>
        </section>
      )}

      {/* =====================================================
          BRANDS
      ====================================================== */}

      {brands.length > 0 && (
        <section className="bg-white border-y border-[#E5E9E4]">

          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14">

            <div className="text-center mb-9">

              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#536D61] mb-2">
                Επιλεγμένες συνεργασίες
              </p>

              <h2 className="font-serif-display text-2xl lg:text-3xl text-[#1E3A2F]">
                {settings?.brands_title ||
                  "Οι μάρκες μας"}
              </h2>

            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-8 items-center">

              {brands.slice(0, 15).map((b) => {

                const inner = b.logo ? (
                  <img
                    src={imgUrl(b.logo)}
                    alt={`${b.name} | Φαρμακείο Κατιρτζόγλου`}
                    className="max-h-12 max-w-full object-contain mx-auto opacity-70 hover:opacity-100 transition-opacity"
                    loading="lazy"
                  />
                ) : (
                  <span className="text-[#536D61] font-medium text-center block">
                    {b.name}
                  </span>
                );

                return b.url ? (
                  <a
                    key={b.id}
                    href={b.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    data-testid={`brand-${b.id}`}
                  >
                    {inner}
                  </a>
                ) : (
                  <div
                    key={b.id}
                    data-testid={`brand-${b.id}`}
                  >
                    {inner}
                  </div>
                );
              })}

            </div>
          </div>
        </section>
      )}

      {/* =====================================================
          WHY CHOOSE US
      ====================================================== */}

      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">

        <div className="text-center max-w-2xl mx-auto mb-10">

          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#536D61] mb-2">
            Η φιλοσοφία μας
          </p>

          <h2 className="font-serif-display text-3xl lg:text-4xl text-[#1E3A2F]">
            Γιατί Φαρμακείο Κατιρτζόγλου;
          </h2>

        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">

          {[
            {
              icon: ShieldCheck,
              title: "Αξιοπιστία",
              text: "Προϊόντα από αξιόπιστες εταιρείες και συνεργάτες.",
            },
            {
              icon: HeartHandshake,
              title: "Προσωπική φροντίδα",
              text: "Η φροντίδα και η εξυπηρέτηση παραμένουν προτεραιότητά μας.",
            },
            {
              icon: Truck,
              title: "Εύκολες αγορές",
              text: "Παραγγείλετε εύκολα online από όπου κι αν βρίσκεστε.",
            },
            {
              icon: Stethoscope,
              title: "Φαρμακευτική γνώση",
              text: "Η εμπειρία του φαρμακείου βρίσκεται πίσω από κάθε επιλογή.",
            },
          ].map((item, index) => {

            const Icon = item.icon;

            return (
              <div
                key={index}
                className="border border-[#E5E9E4] bg-white rounded-sm p-6"
              >

                <div className="w-11 h-11 flex items-center justify-center rounded-sm bg-[#E8F0E1] text-[#1E3A2F]">
                  <Icon className="h-5 w-5" />
                </div>

                <h3 className="mt-5 font-semibold text-[#1E3A2F]">
                  {item.title}
                </h3>

                <p className="mt-2 text-sm text-[#687B73] leading-relaxed">
                  {item.text}
                </p>

              </div>
            );
          })}

        </div>
      </section>

      {/* =====================================================
          STORE / ABOUT
      ====================================================== */}

      {store.enabled !== false &&
        (store.title ||
          store.text ||
          store.image) && (

          <section className="bg-[#E8F0E1]">

            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">

              <div className="relative order-2 lg:order-1">

                <div className="aspect-[4/3] rounded-sm overflow-hidden border border-[#CBD9B6] bg-white">

                  {store.image ? (
                    <img
                      src={imgUrl(store.image)}
                      alt={
                        store.title
                          ? `${store.title} | Φαρμακείο Κατιρτζόγλου`
                          : "Φαρμακείο Κατιρτζόγλου"
                      }
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center">
                      <HeartHandshake className="h-16 w-16 text-[#1E3A2F]/20" />
                    </div>
                  )}

                </div>
              </div>

              <div className="order-1 lg:order-2">

                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#536D61] mb-3">
                  Γνωρίστε μας
                </p>

                <h2 className="font-serif-display text-3xl lg:text-4xl text-[#1E3A2F] leading-tight">
                  {store.title ||
                    "Φαρμακείο Κατιρτζόγλου"}
                </h2>

                <p className="mt-5 text-[#2a3a33] leading-relaxed whitespace-pre-line">
                  {store.text}
                </p>

                <Link
                  to="/page/sxetika-me-emas"
                  className="mt-6 inline-flex items-center gap-2 text-[#1E3A2F] font-medium hover:gap-3 transition-all"
                >
                  Περισσότερα
                  <ArrowRight className="h-4 w-4" />
                </Link>

              </div>
            </div>
          </section>
        )}

      {/* =====================================================
          NEWSLETTER
      ====================================================== */}

      {newsletter.enabled !== false && (
        <section className="max-w-5xl mx-auto px-6 lg:px-12 py-16">

          <div className="bg-[#1E3A2F] rounded-sm px-8 py-12 text-center">

            <Mail className="h-10 w-10 text-[#CBD9B6] mx-auto mb-4" />

            <h2 className="font-serif-display text-3xl text-white">
              {newsletter.title ||
                "Μείνετε ενημερωμένοι"}
            </h2>

            <p className="text-[#CBD9B6] mt-2 max-w-xl mx-auto">
              {newsletter.subtitle ||
                "Νέα προϊόντα, προσφορές και χρήσιμες πληροφορίες από το Φαρμακείο Κατιρτζόγλου."}
            </p>

            <form
              onSubmit={subscribe}
              className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >

              <input
                data-testid="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) =>
                  setEmail(e.target.value)
                }
                placeholder="Το email σας"
                className="flex-1 px-4 py-3 rounded-sm bg-white text-sm outline-none"
              />

              <button
                type="submit"
                data-testid="newsletter-submit"
                className="bg-[#CBD9B6] text-[#1E3A2F] px-6 py-3 rounded-sm font-semibold hover:bg-white transition-colors"
              >
                Εγγραφή
              </button>

            </form>

          </div>
        </section>
      )}

    </div>
  );
}